import React, { PureComponent, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Modal from '../../shared/modals/modal.component';
import FormikProvider from '../../shared/forms/formikProvider.component';
import Form from '../../shared/forms/form.component';
import { TaxRatePropTypes, TaxRatesPropTypes } from '../../shared/propTypes/taxRates';
import { OptionsPropType } from '../../shared/propTypes/common';
import { PeriodPropTypes } from '../shared/propTypes';

import {
  fetchInitialDataForTaxRateModal,
} from './store/actions';
import {
  enabledFlagOptionsSelector,
  countryCodeOptionsSelector,
  plAccountOptionsSelector,
  bsAccountOptionsSelector,
  defPlAccountOptionsSelector,
  defBsAccountOptionsSelector,
  sharedSelectors,
  isFetchingInitialDataForAddAndEditModalSelector,
} from './store/selectors';
import schema from './taxRatesForm.schema';

const ModalTitles = {
  ADD: 'Add Tax Rate',
  EDIT: 'Edit Tax Rate',
};

const defaultValue = '';

class AddTaxRateModal extends PureComponent {
  static propTypes = {
    entities: TaxRatesPropTypes,

    period: PeriodPropTypes.isRequired,
    entityToEdit: TaxRatePropTypes,

    countryCodeOptions: OptionsPropType,
    enabledFlagOptions: OptionsPropType,
    plAccountOptions: OptionsPropType,
    bsAccountOptions: OptionsPropType,
    defPlAccountOptions: OptionsPropType,
    defBsAccountOptions: OptionsPropType,

    hideModal: PropTypes.func.isRequired,

    fetchInitialData: PropTypes.func.isRequired,
    isFetchingInitialData: PropTypes.bool.isRequired,
    saveEntity: PropTypes.func.isRequired,
    isSavingInProgress: PropTypes.bool.isRequired,
    isVisible: PropTypes.bool,
    openForEdit: PropTypes.bool,
  };

  state = {
    addAnother: false,
  };

  componentDidMount() {
    const {
      period,
      fetchInitialData,
    } = this.props;

    fetchInitialData({
      period,
    });
  }

  componentDidUpdate(prevProps) {
    const { isVisible, fetchInitialData, period } = this.props;

    if (!prevProps.isVisible && isVisible) {
      fetchInitialData({ period });
    }
  }

  save = (formData, { setSubmitting, resetForm }) => {
    const { period, saveEntity, openForEdit } = this.props;
    const { addAnother } = this.state;

    const taxRateDataToSave = {
      ...formData,
      periodName: period.name,
      defPlAccount: formData.defPlAccount ? formData.defPlAccount : defaultValue,
      defBsAccount: formData.defBsAccount ? formData.defBsAccount : defaultValue,
      taxRate: formData.taxRate
        ? Number(formData.taxRate) / 100
        : null,
      defTaxRate: formData.defTaxRate
        ? Number(formData.defTaxRate) / 100
        : null,
    };

    this.setState({ addAnother: false }, () => {
      saveEntity(taxRateDataToSave, openForEdit, {
        params: {
          periodName: period.name,
        },
        callback: resetForm,
        keepOpenAfterSaving: addAnother,
      });
      setSubmitting(false);
    });
  };

  renderModal = ({ formik, schema }) => {
    const {
      countryCodeOptions,
      enabledFlagOptions,
      plAccountOptions,
      bsAccountOptions,
      defPlAccountOptions,
      defBsAccountOptions,
      isSavingInProgress,
      openForEdit,
      hideModal,
      isFetchingInitialData,
      isVisible,
    } = this.props;

    const submit = useCallback(() => {
      this.setState({
        addAnother: false,
      }, () => formik.handleSubmit());
    }, [formik]);

    const submitAndAddAnotherAction = useCallback(() => {
      this.setState({
        addAnother: true,
      }, () => formik.handleSubmit());
    }, [formik]);

    const reset = useCallback(() => {
      hideModal();
      formik.resetForm();
    }, [formik, hideModal]);

    return (
      <Modal
        className="add-tax-rate-modal"
        title={openForEdit ? ModalTitles.EDIT : ModalTitles.ADD}
        closeAction={reset}
        dismissText="Cancel"
        dismissAction={reset}
        submitText="Save"
        submitAction={submit}
        isLoading={isFetchingInitialData}
        isSubmitting={formik.isSubmitting || isSavingInProgress}
        submitDisabled={isFetchingInitialData}
        submitAndAddAnotherAction={!openForEdit ? submitAndAddAnotherAction : null}
        submitAndAddAnotherText="Save and Add Another"
        isVisible={isVisible}
      >
        <Form
          formik={formik}
          schema={schema}
          openForEdit={openForEdit}
          formContext={{
            countryCodeOptions,
            enabledFlagOptions,
            plAccountOptions,
            bsAccountOptions,
            defPlAccountOptions,
            defBsAccountOptions,
          }}
        />
      </Modal>
    );
  };


  render() {
    const {
      entityToEdit,
      openForEdit,
      entities,
    } = this.props;

    const formattedEntityToEdit = entityToEdit
      ? {
        ...entityToEdit,
        taxRate: entityToEdit.taxRate
          && String(Number(entityToEdit.taxRate) * 100),
        defTaxRate: entityToEdit.defTaxRate
          && String(Number(entityToEdit.defTaxRate) * 100),
      }
      : null;

    return (
      <FormikProvider
        schema={schema}
        entityToEdit={formattedEntityToEdit}
        onSubmit={this.save}
        render={this.renderModal}
        validationContext={{
          entities,
          openForEdit,
        }}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    countryCodeOptions: countryCodeOptionsSelector(state),
    enabledFlagOptions: enabledFlagOptionsSelector(state),
    plAccountOptions: plAccountOptionsSelector(state),
    bsAccountOptions: bsAccountOptionsSelector(state),
    defPlAccountOptions: defPlAccountOptionsSelector(state),
    defBsAccountOptions: defBsAccountOptionsSelector(state),
    entities: sharedSelectors.entitiesSelector(state),
    isSavingInProgress: sharedSelectors.savingEntitySelector(state),
    isFetchingInitialData: isFetchingInitialDataForAddAndEditModalSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialData: fetchInitialDataForTaxRateModal,
  }
)(AddTaxRateModal);
